import moment from "moment";
import _ from "lodash";
import numeral from "numeral";

import { normalizeRevenue } from "helpers/Chart";
import { getCurrencySymbol } from "helpers/Utils";
import {
  UNFILLED,
  DEMAND_TYPES,
  getGuarenteedDemandTypes,
} from "constants/DemandType";
import { getDateList } from "helpers/DateTransform";
import { TableRowContent } from "./TableRowContent";

export const TYPE = {
  NETWORK: "NETWORK",
  YIELD_SET: "YIELD_SET",
  AD_UNITS: "AD_UNITS",
};

export const GROUP = {
  BM: "bm",
  O: "o",
};

export const METRIC = {
  REVENUE: "rev",
  IMPRESSIONS: "imp",
};

export const VIEW = {
  GROUP: "Groups",
  ALL: "All",
  BILLABLE: "Billable",
  GUARANTEED: "Guaranteed",
  NON_BILLABLE: "Non-Billable",
};

export const dpGroupList = [
  VIEW.GUARANTEED,
  VIEW.BILLABLE,
  VIEW.NON_BILLABLE,
  UNFILLED,
];

export function getDataParams({
  networkId,
  yieldsetId,
  unitInfo,
  selectedType,
  startDate,
  endDate,
}) {
  return {
    dpParam: {
      type: selectedType,
      networkId: networkId,
      yieldSetId: yieldsetId,
      extGamUnitIds: unitInfo.map((unit) => unit.extGamUnitId),
      startDate: startDate,
      endDate: endDate,
    },
    trendParam:
      selectedType === TYPE.AD_UNITS
        ? {
            extGamUnitId: unitInfo.map((unit) => unit.extGamUnitId),
            startDate,
            endDate,
            reportType: "NET_BILLABLE_REV",
          }
        : {
            networkId,
            startDate,
            endDate,
            yieldSetId: selectedType === TYPE.YIELD_SET ? yieldsetId : null,
          },
  };
}

export function compareParams(param1, param2) {
  return _.isEqualWith(param1, param2, (val1, val2) => {
    if (_.isNil(val1) && _.isNil(val2)) {
      return true;
    }

    return undefined;
  });
}

export function parseDemandPortfolioData({
  trendResult,
  dpResult,
  selectedType,
  startDate,
  endDate,
  networkInfo,
}) {
  // [{ date: "YYYY-MM-DD", lift: number }]
  const liftData = trendResult.map((item) => ({
    date: item.date,
    lift:
      selectedType === TYPE.AD_UNITS
        ? item.net_billable_lift
        : item.billable_lift_net,
  }));

  const dateList = getDateList(
    startDate.split(" ")[0],
    endDate.split(" ")[0],
    true
  );

  const dataKey = [];
  _.values(GROUP).forEach((group) => {
    _.values(METRIC).forEach((metric) => {
      const key = `${group}_${metric}`;
      dataKey.push(key);
    });
  });

  const groupedData = _.groupBy(dpResult, "demand_type");

  // [demandType]:{ [dataKey]: [] as value by date }
  const result = _.mapValues(groupedData, (items) => {
    return _.mapValues(_.keyBy(dataKey), (key) => {
      return _.map(dateList, (date) => {
        const item = _.find(items, { date: date });
        return item ? item[key] : 0;
      });
    });
  });

  const grouped = _.transform(
    dpGroupList,
    (res, view) => {
      const keys = getDemandTypeByView({ view, networkInfo });
      if (!keys) return;

      res[view] = _.transform(
        keys,
        (acc, key) => {
          _.forEach(result[key], (values, field) => {
            acc[field] = acc[field]
              ? _.zipWith(acc[field], values, _.add)
              : [...values];
          });
        },
        {}
      );
    },
    {}
  );

  return {
    data: result,
    groupedData: grouped,
    dateList,
    liftData,
  };
}

export function getDemandTypeByView({ view, networkInfo }) {
  const allDemandTypes = _.values(DEMAND_TYPES);

  switch (view) {
    case VIEW.ALL:
      return [...allDemandTypes, UNFILLED];
    case VIEW.BILLABLE:
      return _.get(networkInfo, "billableDemandTypes", []);
    case VIEW.GUARANTEED:
      return getGuarenteedDemandTypes();
    case VIEW.NON_BILLABLE:
      const guaranteed = getGuarenteedDemandTypes();
      const billable = _.get(networkInfo, "billableDemandTypes", []);
      const excluded = new Set([...guaranteed, ...billable]);
      return allDemandTypes.filter((dt) => !excluded.has(dt));
    case UNFILLED:
      return [UNFILLED];
    default:
      return [];
  }
}

export function getGroupColorSet({ networkInfo, colorSet }) {
  return new Map([
    ...dpGroupList.map((group) => {
      const keys = getDemandTypeByView({ view: group, networkInfo });
      const color =
        keys.length > 0 && colorSet.get(keys[0])
          ? colorSet.get(keys[0])
          : "#C1C1C1";
      return [group, color];
    }),
  ]);
}

export function getChartOption({
  data,
  dateList,
  list,
  metric,
  currency,
  colorSet,
  colorLift,
  isGroup,
  liftData,
  twdRate,
  metricOptions,
}) {
  const valueFormat = metric === METRIC.REVENUE ? "0,0.00" : "0,0";
  const parsedMetric = _.find(metricOptions, { value: metric });
  const prefix = metric === METRIC.REVENUE ? getCurrencySymbol(currency) : "";

  const liftMap = _.keyBy(liftData, "date");
  const liftSeries = dateList.map((date) => {
    const liftItem = liftMap[date];
    return liftItem ? _.round(liftItem.lift * 100, 2) : 0;
  });

  return {
    chart: {
      type: "column",
      height: 280,
      animation: false,
      spacingRight: 74,
      marginRight: 84,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: dateList,
    },
    yAxis: [
      {
        title: {
          text: `${parsedMetric ? parsedMetric.label : ""}${
            metric === METRIC.REVENUE ? ` (${currency})` : ""
          }`,
        },
      },
      {
        title: {
          text: "Billable Revenue Lift %",
        },
        labels: {
          format: "{value} %",
        },
        min: 0,
        max: 100,
        opposite: true,
        endOnTick: false,
      },
    ],
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          menuItems: ["viewFullscreen"],
        },
      },
    },
    tooltip: {
      shared: true,
      hideDelay: 0,
      animation: false,
      useHTML: true,
      backgroundColor: "rgba(255,255,255,1)", // use full white to make looking at all those numbers easier to read
      formatter: function () {
        const chart = this.points[0].series.chart;
        const point = this.points[0].point;

        let html = `<b>${moment(point.category).format(
          "YYYY-MM-DD ddd"
        )}</b><br/>`;

        const sList = [];
        const cList = [];
        const valList = {
          b: [],
          o: [],
        };
        const percList = {
          b: [],
          o: [],
        };

        let lift = 0;
        chart.series.forEach((s) => {
          const p = s.data[point.index];
          if (p && typeof p.y === "number") {
            if (_.isNil(s.options.stack)) {
              lift = p.y;
            } else {
              if (_.indexOf(sList, s.name) === -1) {
                sList.push(s.name);
                cList.push(p.color);
              }

              valList[s.options.stack].push(p.y);
              percList[s.options.stack].push(p.percentage || 0);
            }
          }
        });

        html += `<div><b>Billable Revenue Lift: <span style="color: ${colorLift}">${numeral(
          lift
        ).format("0.00")}%</span></b></div>`;

        html += `<table><thead><tr><th>${
          isGroup ? "Group" : "Demand Type"
        }</th><th colspan=2 style="text-align: center;">b</th>
                  <th colspan=2 style="text-align: center;">o</th></tr></thead><tbody>
          ${sList
            .map((s, i) =>
              TableRowContent({
                color: cList[i],
                name: s,
                group: [
                  {
                    value: `${prefix}${numeral(valList.b[i]).format(
                      valueFormat
                    )}`,
                    percentage: numeral(percList.b[i]).format("0.0") + "%",
                  },
                  {
                    value: `${prefix}${numeral(valList.o[i]).format(
                      valueFormat
                    )}`,
                    percentage: numeral(percList.o[i]).format("0.0") + "%",
                  },
                ],
              })
            )
            .join("")}
            <tr>
                  <td><b>Total</b></td>
                  <td style="text-align: right; padding-left: 6px"><b>${prefix}${numeral(
          _.sum(valList.b)
        ).format(valueFormat)}</b></td>
                  <td style="text-align: right; padding-left: 4px"></td>
                  <td style="text-align: right; padding-left: 6px"><b>${prefix}${numeral(
          _.sum(valList.o)
        ).format(valueFormat)}</b></td>
                  <td style="text-align: right; padding-left: 4px"></td>
                </tr></tbody></table>`;

        return html;
      },
    },
    plotOptions: {
      column: {
        stacking: "percent",
        states: {
          hover: {
            enabled: false,
          },
        },
      },
      series: {
        states: {
          inactive: {
            enabled: false,
          },
        },
      },
    },
    series: [
      ..._.flattenDeep(
        list.map((dt) => {
          const isHide = !data[dt];
          return _.values(GROUP).map((group) => {
            const temp =
              data[dt] && data[dt][`${group}_${metric}`]
                ? data[dt][`${group}_${metric}`]
                : [];
            const parsed =
              metric === METRIC.REVENUE
                ? temp.map(
                    (v) =>
                      normalizeRevenue(v) * (currency === "TWD" ? twdRate : 1)
                  )
                : temp;
            return {
              ...(group === GROUP.BM ? { id: dt } : {}),
              name: dt,
              data: parsed,
              yAxis: 0,
              type: "column",
              color: colorSet.get(dt),
              stack: group[0],
              ...(group === "o" ? { linkedTo: dt, showInLegend: false } : {}),
              dataLabels: {
                enabled: true,
                verticalAlign: "top",
                inside: false,
                formatter: function () {
                  if (this.series.index === 0 || this.series.index === 1)
                    return this.series.options.stack;
                },
              },
              visible: isHide ? false : true,
              disabled: isHide ? true : false,
            };
          });
        })
      ),
      {
        name: "Billable Revenue Lift %",
        type: "line",
        yAxis: 1,
        data: liftSeries,
        tooltip: {
          valueSuffix: "%",
        },
        color: colorLift,
      },
    ],
  };
}

export function getLineChartOption({
  data,
  dateList,
  group,
  list,
  metric,
  currency,
  colorSet,
  twdRate,
  metricOptions,
}) {
  const valueFormat = metric === METRIC.REVENUE ? "0,0.00" : "0,0";
  const parsedMetric = _.find(metricOptions, { value: metric });
  const prefix = metric === METRIC.REVENUE ? getCurrencySymbol(currency) : "";

  return {
    chart: {
      height: 280,
    },
    title: {
      text: _.values(GROUP).includes(group) ? group[0] : "Total (b+o)",
      align: "left",
    },
    yAxis: {
      title: {
        text: `${parsedMetric ? parsedMetric.label : ""}${
          metric === METRIC.REVENUE ? ` (${currency})` : ""
        }`,
      },
    },
    xAxis: {
      categories: dateList,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        const chart = this.points[0].series.chart;
        const point = this.points[0].point;

        let html = `<b>${moment(point.category).format(
          "YYYY-MM-DD ddd"
        )}</b><br/>`;

        html += `<table style="border-collapse: separate; border-spacing: 0"><thead><tr><th>${chart.title.textStr}</th>
                  <th></th><th></th></tr></thead><tbody>`;

        const sum = _.sum(
          chart.series.map((s) => _.get(s, ["data", point.index, "y"], 0))
        );
        chart.series.forEach((s) => {
          const p = s.data[point.index];
          if (p && typeof p.y === "number") {
            const value = `${prefix}${numeral(p.y).format(valueFormat)}`;
            html += TableRowContent({
              color: p.color,
              name: s.name,
              group: [
                {
                  value,
                  percentage: numeral(p.y / sum).format("0.0%"),
                },
              ],
            });
          }
        });

        html += `</tbody></table>`;

        return html;
      },
    },
    plotOptions: {
      column: {
        states: {
          hover: {
            enabled: false,
          },
        },
      },
      series: {
        states: {
          inactive: {
            enabled: false,
          },
        },
      },
    },
    legend: {
      layout: "vertical",
      align: "right",
      verticalAlign: "middle",
    },
    series: list.map((dt) => {
      const dtData = data[dt];
      let parsedData = [];
      if (dtData) {
        if (_.values(GROUP).includes(group)) {
          const temp = dtData[`${group}_${metric}`] || [];
          parsedData =
            metric === METRIC.REVENUE
              ? temp.map(
                  (v) =>
                    normalizeRevenue(v) * (currency === "TWD" ? twdRate : 1)
                )
              : temp;
        } else {
          const groupedData = _.values(GROUP).map((g) => {
            return dtData[`${g}_${metric}`] || [];
          });
          const zippedData = _.zipWith(...groupedData, (...vals) => {
            return _.sum(vals);
          });
          parsedData =
            metric === METRIC.REVENUE
              ? zippedData.map(
                  (v) =>
                    normalizeRevenue(v) * (currency === "TWD" ? twdRate : 1)
                )
              : zippedData;
        }
      }

      return {
        name: dt,
        data: parsedData,
        color: colorSet.get(dt),
        visible: !dtData ? false : true,
        disabled: !dtData ? true : false,
      };
    }),
  };
}

export function getPieChartOption({
  data,
  dateList,
  list,
  metric,
  currency,
  colorSet,
  group,
  twdRate,
}) {
  const valueFormat = metric === METRIC.REVENUE ? "0,0.00" : "0,0";
  const prefix = metric === METRIC.REVENUE ? getCurrencySymbol(currency) : "";

  return {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: "pie",
      height: 280,
    },
    title: {
      text: `${group[0]}`,
      align: "left",
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const point = this.point;
        return `<b>${point.name}</b>:<br/> <b>${numeral(
          point.percentage
        ).format("0.0")}%</b><br/>${prefix}${numeral(point.y).format(
          valueFormat
        )}`;
      },
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    legend: {
      layout: "vertical",
      align: "right",
      verticalAlign: "middle",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          distance: -40,
          format: "{point.percentage:.1f}%",
          style: {
            fontSize: "1.2em",
            textOutline: "none",
            opacity: 0.7,
          },
          filter: {
            operator: ">",
            property: "percentage",
            value: 10,
          },
        },
        showInLegend: true,
      },
    },
    series: [
      {
        name: "",
        colorByPoint: true,
        data: list
          .map((dt) => {
            const dtData = data[dt];
            let avg = 0;
            if (dtData) {
              const temp = dtData[`${group}_${metric}`] || [];
              avg =
                _.sum(
                  metric === METRIC.REVENUE
                    ? temp.map(
                        (v) =>
                          normalizeRevenue(v) *
                          (currency === "TWD" ? twdRate : 1)
                      )
                    : temp
                ) / dateList.length;
            }

            return {
              name: dt,
              y: avg,
              color: colorSet.get(dt),
              visible: !dtData ? false : true,
              disabled: !dtData ? true : false,
            };
          })
          .sort((a, b) => {
            if (a.y !== b.y) {
              return b.y - a.y;
            }
            return Number(b.visible) - Number(a.visible);
          }),
      },
    ],
  };
}

export function getBarChartOption({
  data,
  dateList,
  list,
  metric,
  currency,
  colorSet,
  group,
  twdRate,
  metricOptions,
}) {
  const valueFormat = metric === METRIC.REVENUE ? "0,0.00" : "0,0";
  const parsedMetric = _.find(metricOptions, { value: metric });
  const prefix = metric === METRIC.REVENUE ? getCurrencySymbol(currency) : "";

  const total = _.reduce(
    _.values(data),
    (sum, dData) => {
      const tempSum = _.sum(
        (dData[`${group}_${metric}`] || []).filter((d) => !_.isNil(d))
      );
      return sum + tempSum;
    },
    0
  );

  return {
    chart: {
      type: "bar",
      height: 280,
      animation: false,
      spacingRight: 14,
      marginRight: 24,
    },
    title: {
      text: `${group[0]}`,
      align: "left",
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        const chart = this.points[0].series.chart;

        let html = `<table style="border-collapse: separate; border-spacing: 0"><thead><tr><th>${chart.title.textStr}</th>
                  <th style="text-align: right">Daily Avg.</th><th></th></tr></thead><tbody>`;

        chart.series[0].points.forEach((p) => {
          if (p && typeof p.y === "number") {
            const value = `${prefix}${numeral(p.y).format(valueFormat)}`;
            html += TableRowContent({
              color: p.color,
              name: p.name,
              group: [
                {
                  value,
                  percentage: numeral(p.percentage).format("0.0%"),
                },
              ],
            });
          }
        });

        html += `</tbody></table>`;

        return html;
      },
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          formatter: function () {
            return this.point.percentage
              ? `${numeral(this.point.percentage).format("0.0%")}`
              : "";
          },
        },
      },
    },
    xAxis: {
      type: "category",
      title: {
        text: null,
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: `${parsedMetric ? parsedMetric.label : ""}${
          metric === METRIC.REVENUE ? ` (${currency})` : ""
        }`,
      },
    },
    series: [
      {
        name: "",
        data: list
          .map((dt) => {
            const dtData = data[dt];
            let sum = 0;
            let avg = 0;
            let percentage = 0;
            if (dtData) {
              const temp = dtData[`${group}_${metric}`] || [];
              sum = _.sum(
                metric === METRIC.REVENUE
                  ? temp.map(
                      (v) =>
                        normalizeRevenue(v) * (currency === "TWD" ? twdRate : 1)
                    )
                  : temp
              );
              avg = sum / dateList.length;

              percentage = total > 0 ? _.sum(temp) / total : 0;
            }

            return {
              name: dt,
              y: avg,
              sum,
              percentage,
              color: colorSet.get(dt),
              visible: !dtData ? false : true,
              disabled: !dtData ? true : false,
            };
          })
          .sort((a, b) => {
            if (a.y !== b.y) {
              return b.y - a.y;
            }
            return Number(b.visible) - Number(a.visible);
          }),
      },
    ],
  };
}
